import { motion } from 'framer-motion';
import {
  Presentation,
  Users,
  Wallet,
  CalendarCheck,
  ClipboardList,
  BarChart3,
  MessageSquare,
  FileText,
  Video,
  UserPlus,
  Bell,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function CoachingPage() {
  const features = [
    {
      icon: Users,
      title: 'Batch & Class Management',
      description:
        'Create and manage multiple batches with subjects, timings, and faculty allocation. Handle morning, evening, and weekend batches effortlessly.',
      color: 'bg-blue-500',
    },
    {
      icon: UserPlus,
      title: 'Enquiry & Admission Management',
      description:
        'Capture leads, follow up on enquiries, and convert them into admissions. Track every prospect from first call to enrollment.',
      color: 'bg-purple-500',
    },
    {
      icon: Wallet,
      title: 'Fee Collection & Reminders',
      description:
        'Collect fees online or offline with installment support. Send automated fee reminders and generate receipts instantly.',
      color: 'bg-emerald-500',
    },
    {
      icon: CalendarCheck,
      title: 'Attendance Tracking',
      description:
        'Mark student attendance batch-wise in seconds. Notify parents automatically when a student is absent.',
      color: 'bg-pink-500',
    },
    {
      icon: ClipboardList,
      title: 'Test Series & Assessments',
      description:
        'Conduct online and offline tests, publish results, and share detailed scorecards with students and parents.',
      color: 'bg-yellow-500',
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description:
        'Track student progress across tests and subjects. Identify weak areas and help every student improve their rank.',
      color: 'bg-cyan-600',
    },
    {
      icon: Video,
      title: 'Online & Recorded Classes',
      description:
        'Host live classes and share recorded lectures so students never miss a session, even from home.',
      color: 'bg-indigo-500',
    },
    {
      icon: FileText,
      title: 'Study Material Distribution',
      description:
        'Share notes, DPPs, practice papers, and PDFs with specific batches in one click, organized by subject and topic.',
      color: 'bg-green-500',
    },
    {
      icon: MessageSquare,
      title: 'Parent & Student Communication',
      description:
        'Send updates, test results, and announcements via WhatsApp and in-app notifications to keep everyone informed.',
      color: 'bg-orange-500',
    },
    {
      icon: Bell,
      title: 'Timetable & Schedule Alerts',
      description:
        'Publish batch timetables and send automatic alerts for class changes, extra classes, and upcoming tests.',
      color: 'bg-red-500',
    },
  ];

  const deliverables = [
    'Batch, faculty, and timetable management from a single dashboard',
    'Enquiry-to-admission pipeline with follow-up tracking',
    'Fee management with online payments, installments, and receipts',
    'Test series, results, and student performance analytics',
    'Student and parent apps with WhatsApp notifications',
  ];

  return (
    <div data-testid="coaching-page" className="pt-20">
      <section className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 mb-6">
              <Presentation className="h-4 w-4 text-emerald-light" />
              <span className="text-sm font-medium text-slate-100">
                Coaching Management Solution
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black mb-6 font-heading">
              Run Your Coaching Institute Smarter
            </h1>
            <p className="text-base leading-relaxed text-slate-300 max-w-3xl mx-auto mb-8">
              Manage batches, admissions, fees, tests, and communication from one
              powerful platform. Built for coaching centers, tuition classes, and
              training institutes of every size.
            </p>
            <Link to="/register">
              <Button
                data-testid="coaching-demo-button"
                size="lg"
                className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
              >
                Book Free Demo
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section
        data-testid="coaching-features-grid-section"
        className="py-24 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              Everything Your Coaching Center Needs
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              A complete management solution designed for Indian coaching institutes
              and tuition classes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                data-testid={`coaching-feature-module-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div
                  className={`${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-navy mb-3 font-heading">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-12 shadow-sm">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
              Core Deliverables
            </h2>
            <div className="space-y-4">
              {deliverables.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="coaching-cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            Ready To Grow Your Coaching Institute?
          </h2>
          <p className="text-base text-slate-600 mb-8">
            Book a personalized demo to see how our Coaching Management Solution
            can simplify operations and boost results at your institute.
          </p>
          <Link to="/register">
            <Button
              data-testid="coaching-cta-button"
              size="lg"
              className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
            >
              Book Free Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
